import React from 'react';

import cn from 'classnames';
import PropTypes from 'prop-types';
import { CgSpinner } from 'react-icons/cg';
import { Link } from 'react-router-dom';

const Button = React.forwardRef((props, ref) => {
  const {
    label,
    isLoading,
    to,
    icon,
    trailingItem,
    withLoader,
    withScrollOnError,
    withLoaderV2,
    ...rest
  } = props;

  const renderLabel = () => {
    if (icon || trailingItem) {
      return (
        <div className="flex items-center gap-ds-8">
          {icon}
          {label}
          {trailingItem}
        </div>
      );
    }

    return label;
  };

  if (to) {
    return (
      <Link
        to={to}
        disabled={isLoading}
        {...rest}
        className={cn('duration-300 ease-out-in transition', {
          [`${rest?.className}`]: rest?.className,
          '!cursor-wait': isLoading,
        })}
      >
        {renderLabel()}
      </Link>
    );
  }

  const handleOnClick = (e) => {
    if (rest.onClick) rest.onClick(e);

    if (!withScrollOnError) return;

    setTimeout(() => {
      let el;

      if (typeof withScrollOnError === 'string') {
        el = document.querySelector(withScrollOnError);
        const topPos = el?.querySelector('.form-error-badge')?.offsetTop || 0;
        el?.scrollTo({ top: topPos / 2, behavior: 'smooth' });
        return;
      }

      el = document.querySelector('.form-error-badge');
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
  };

  return (
    <button
      ref={ref}
      type="button"
      disabled={isLoading}
      {...rest}
      onClick={handleOnClick}
      className={cn('duration-300 ease-out-in transition', {
        [`${rest?.className}`]: rest?.className,
        '!cursor-wait': isLoading,
      })}
    >
      {isLoading && withLoaderV2 ? (
        <div
          className={cn(
            'absolute top-0 left-0 h-full w-full text-center flex',
            `${rest?.className}`
          )}
          style={{ inset: 'auto' }}
        >
          <CgSpinner className="h-5 w-5 animate-spin m-auto" />
        </div>
      ) : null}

      {isLoading && withLoader ? 'Loading...' : renderLabel()}
    </button>
  );
});

Button.defaultProps = {
  label: 'Name',
  isLoading: false,
  withLoader: false,
  withLoaderV2: false,
  to: false,
  icon: false,
  trailingItem: false,
  withScrollOnError: false,
};

Button.propTypes = {
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
    PropTypes.element,
    PropTypes.instanceOf(Function),
  ]),
  isLoading: PropTypes.bool,
  withScrollOnError: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  withLoader: PropTypes.bool,
  withLoaderV2: PropTypes.bool,
  icon: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.element,
    PropTypes.instanceOf(Object),
  ]),
  trailingItem: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.element,
    PropTypes.string,
    PropTypes.instanceOf(Object),
    PropTypes.instanceOf(Function),
  ]),
  to: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
    PropTypes.instanceOf(Object),
  ]),
};

export default Button;
